import { makeHypervisorClient } from './HypervisorServiceUtils';

// Opaque identifier used by consumers when composing SWR keys. Not a real URI
// — the service functions build their own fully-qualified orchestrator URLs
// internally. Exported so callers can reuse the same literal in their SWR key
// objects; SWR invalidation should use the full key object (or a predicate),
// not just this string by itself.
export const vpnGatewaysCacheKey = 'vpnGateways';

export interface IVpnGatewayTrustedCertificate {
    Name: string;
    PublicCertificateData: string;
}

export interface IVpnGatewayData {
    Key: string;
    Name: string;
    Description: string;
    VnetAddressSpace: string;
    VnetGatewaySubnetAddressSpace: string;
    PublicIp: string;
    ResourceId: string;
    State: string;
    Sku: string;
    Location: string;
    InsertedDateTime: string;
    DnsServers: string[];
    Connections: IVpnGatewayConnectionPeering[];
    Peerings: IVpnGatewayConnectionPeering[];
    PrivateEndpoints?: IGatewayPrivateEndpointDto[];
    PrivateEndpointNames?: string[];
    MaxUserConnections: number;
    Freezing: FreezingMetadata;
    GatewayType: string;
    BGPEnabled: boolean;
    ASN: number | null;
    PrimaryBGPAddress: string | null;
    PrimaryApipaAddresses: string[];
    TrustedCertificates?: IVpnGatewayTrustedCertificate[];
    Nva?: IVpnGatewayNvaDto;
}

interface FreezingMetadata {
    Frozen: boolean;
    DaysToFreeze: number;
    FreezingValidationMessage?: string;
}

export interface IVpnGatewaysResponse {
    value: IVpnGatewayData[];
}

export interface ICreateEditVpnGatewayData {
    Name: string;
    VnetAddressSpace?: string;
    dnsServers: Array<{ dnsServer: string }>;
    primaryApipaAddresses: Array<{ apipaAddress: string }>;
    trustedCertificates: Array<{ name: string; publicCertificateData: string }>;
    Sku?: string;
    Type?: string;
}

export interface ICreateEditVpnGatewayPayload {
    Name: string;
    VnetAddressSpace?: string;
    DnsServers?: string[];
    PrimaryApipaAddresses?: string[];
    TrustedCertificates?: IVpnGatewayTrustedCertificate[];
    Sku?: string;
    Type?: string;
}

export interface IVpnGatewayConnectionPeering {
    Key: string;
    Name: string;
    TargetIpAddress: string;
    TargetAddressSpaces: string[];
    State: string;
    Internal?: boolean;
    ExpressRouteTargetResourceId?: string;
    IpSecPolicy?: IpSecPolicy;
    BGPEnabled: boolean;
    BGPPeerAddress: string | null;
    PrimaryCustomBGPAddress: string | null;
    ASN: number | null;
}

// DTO for private endpoints returned by Hypervisor API
export interface IGatewayPrivateEndpointDto {
    Id: string;
    Name: string;
    TargetResourceId?: string;
    PrivateEndpointNames?: string[];
    State: string;
}

type VpnGatewayNvaState = 'Provisioning' | 'Failed' | 'Deployed' | 'Deploying' | 'Deleting';

export type VpnGatewayNvaPeeringState =
    | 'None'
    | 'Provisioning'
    | 'Connected'
    | 'Failed'
    | 'AuthFailed';

export interface IVpnGatewayNvaDto {
    Id: string;
    State: VpnGatewayNvaState;
    NextHopIpAddress: string | null;
    PrivateIpAddress: string | null;
    TargetVnetResourceId?: string | null;
    HubVnetResourceId?: string | null;
    ReverseBgpDummyVnetResourceId?: string | null;
    Routes: string[];
    AutoPeerHub?: boolean;
    AutoPeerBgp?: boolean;
    HubPeeringState?: VpnGatewayNvaPeeringState;
    BgpPeeringState?: VpnGatewayNvaPeeringState;
    SubscriptionKey?: string | null;
}

export interface INvaCreatePayload {
    Routes?: string[];
    NextHopIpAddress?: string;
    TargetVnetResourceId?: string;
    SubscriptionKey?: string;
    AutoPeerHub?: boolean;
    AutoPeerBgp?: boolean;
}

export interface ICloudServiceSubscriptionDto {
    Key: string;
    Name: string;
    Description?: string;
    InsertedDateTime: string;
    UpdatedDateTime: string;
    SubscriptionId?: string;
    TenantId?: string;
    ClientId?: string;
    '@odata.type'?: string;
}

export interface IVpnGatewayConnectionFormData {
    Name: string;
    SharedKey: string;
    AuthKey: string;
    TargetIpAddress: string;
    addressSpaces: Array<{ addressSpace: string }>;
    ExpressRouteTargetResourceId?: string;
    IpSecPolicy?: IpSecPolicy;
    BGPEnabled: boolean;
    BGPPeerAddress: string;
    ASN: string;
}

export interface IVpnGatewayConnectionCreateEditPayload {
    SharedKey?: string;
    Name: string;
    TargetIpAddress?: string;
    TargetAddressSpaces?: string[];
    Internal?: boolean;
    ExpressRouteTargetResourceId?: string;
    IpSecPolicy?: IpSecPolicy;
    BGPEnabled?: boolean;
    BGPPeerAddress?: string | null;
    ASN?: number | null;
}

export interface IPrivateEndpointCreatePayload {
    Name: string;
    TargetResourceId?: string;
    PrivateEndpointNames?: string[];
    RequestMessage?: string;
}

interface IPrivateEndpointEditPayload {
    Name: string;
    PrivateEndpointNames?: string[];
}

export interface IpSecPolicy {
    SaLifeTimeSeconds: number;
    SaDataSizeKilobytes: number;
    IPsecEncryption: string;
    IPsecIntegrity: string;
    IkeEncryption: string;
    IkeIntegrity: string;
    DhGroup: string;
    PfsGroup: string;
}
interface IVpnGatewayTenantConfiguration {
    ValidationError: string | undefined;
    CanCreateVpnGateway: boolean;
}

// OData expand is kept as a literal query string (not `urlParams`) because
// orchestrator/OData expects literal `$expand` — URLSearchParams encodes `$`
// as `%24`, which some OData servers reject.
const VPN_GATEWAYS_EXPAND_QUERY = '?$expand=Connections,PrivateEndpoints,Nva&expand=Peerings';

export function getAllVpnGateways({
    accountLogicalName, selectedTenantId,
}: { requestUri?: string; accountLogicalName: string; selectedTenantId: string }) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .get<IVpnGatewaysResponse>(`/VpnGateways${VPN_GATEWAYS_EXPAND_QUERY}`);
}

// The server proxy for `getVpnGateway` forwarded to the exact same URL as
// `getAllVpnGateways` — the server ignored `vpnGatewayKey`/`expand` query
// params when calling orchestrator. We preserve that behavior here so SWR
// callers keep their distinct cache keys but hit the same endpoint.
export function getVpnGateway({
    accountLogicalName, selectedTenantId,
}: {
    requestUri?: string; accountLogicalName: string;
    selectedTenantId: string; vpnGatewayKey: string; expand?: string; }) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .get<IVpnGatewaysResponse>(`/VpnGateways${VPN_GATEWAYS_EXPAND_QUERY}`);
}

export function getVpnGatewayTenantConfig({
    accountLogicalName, selectedTenantId,
}: { url: string; accountLogicalName: string; selectedTenantId: string }) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .get<IVpnGatewayTenantConfiguration>('/VpnGateways/TenantConfiguration');
}

export function createVpnGateway(body: ICreateEditVpnGatewayPayload, accountLogicalName: string, selectedTenantId: string) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .post<ICreateEditVpnGatewayPayload>('/VpnGateways/CreateVpnGateway', body);
}

export function editVpnGateway(body: ICreateEditVpnGatewayPayload,
    accountLogicalName: string, selectedTenantId: string, vpnGatewayKey: string) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .post<ICreateEditVpnGatewayPayload>(`/VpnGateways/${vpnGatewayKey}/EditVpnGateway`, body);
}

export function deleteVpnGateway(accountLogicalName: string, selectedTenantId: string, vpnGatewayKey: string) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .delete(`/VpnGateways/${vpnGatewayKey}`);
}

export function redeployVpnGateway(accountLogicalName: string, selectedTenantId: string, vpnGatewayKey: string) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .post<IVpnGatewaysResponse>(`/VpnGateways/${vpnGatewayKey}/Redeploy`);
}

export function createVpnGatewayConnection(body: IVpnGatewayConnectionCreateEditPayload,
    accountLogicalName: string, selectedTenantId: string, vpnGatewayKey: string) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .post<IVpnGatewayConnectionCreateEditPayload>(`/VpnGateways/${vpnGatewayKey}/CreateConnection`, body);
}

export function editVpnGatewayConnection(body: IVpnGatewayConnectionCreateEditPayload,
    accountLogicalName: string, selectedTenantId: string, vpnGatewayConnectionKey: string) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .post<IVpnGatewayConnectionCreateEditPayload>(`/VpnGatewayConnections/${vpnGatewayConnectionKey}/EditConnection`, body);
}

export function deleteVpnGatewayConnection(accountLogicalName: string, selectedTenantId: string, vpnGatewayConnectionKey: string) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .delete(`/VpnGatewayConnections/${vpnGatewayConnectionKey}`);
}

export function getVpnGatewayConnection({
    accountLogicalName, selectedTenantId, vpnGatewayConnectionKey,
}: { url: string; accountLogicalName: string; selectedTenantId: string; vpnGatewayConnectionKey: string }) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .get<IVpnGatewayConnectionPeering>(`/VpnGatewayConnections/${vpnGatewayConnectionKey}`);
}

export function refreshVpnGatewayConnection(accountLogicalName: string, selectedTenantId: string, vpnGatewayConnectionKey: string) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .post<IVpnGatewayConnectionPeering>(`/VpnGatewayConnections/${vpnGatewayConnectionKey}/RefreshConnection`);
}

export function createVpnGatewayPrivateEndpoint(body: IPrivateEndpointCreatePayload,
    accountLogicalName: string, selectedTenantId: string, vpnGatewayKey: string) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .post<IPrivateEndpointCreatePayload>(`/VpnGateways/${vpnGatewayKey}/CreateGatewayPrivateEndpoint`, body);
}

export function editVpnGatewayPrivateEndpoint(body: IPrivateEndpointEditPayload,
    accountLogicalName: string, selectedTenantId: string, privateEndpointId: string) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .post<IGatewayPrivateEndpointDto>(`/GatewayPrivateEndpoints/${privateEndpointId}/EditGatewayPrivateEndpoint`, body);
}

export function deleteVpnGatewayPrivateEndpoint(accountLogicalName: string, selectedTenantId: string, privateEndpointId: string) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .delete(`/GatewayPrivateEndpoints/${privateEndpointId}`);
}

export function refreshVpnGatewayPrivateEndpoint(accountLogicalName: string, selectedTenantId: string, privateEndpointId: string) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .post<IGatewayPrivateEndpointDto>(`/GatewayPrivateEndpoints/${privateEndpointId}/RefreshGatewayPrivateEndpoint`);
}

export function createVpnGatewayNva(body: INvaCreatePayload, accountLogicalName: string, selectedTenantId: string, vpnGatewayKey: string) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .post<IVpnGatewayNvaDto>(`/VpnGateways/${vpnGatewayKey}/CreateNva`, body);
}

export function editVpnGatewayNva(body: INvaCreatePayload, accountLogicalName: string, selectedTenantId: string, nvaId: string) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .post<IVpnGatewayNvaDto>(`/VpnGatewayNvas/${nvaId}/EditNva`, body);
}

export function deleteVpnGatewayNva(accountLogicalName: string, selectedTenantId: string, nvaId: string) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .delete(`/VpnGatewayNvas/${nvaId}`);
}

export function getCloudServiceSubscriptions(accountLogicalName: string, selectedTenantId: string) {
    return makeHypervisorClient(accountLogicalName, selectedTenantId)
        .get<{ value: ICloudServiceSubscriptionDto[] }>('/Subscriptions');
}
